import piexif from "piexifjs";
import Tiff from "tiff.js";
import heic2any from "heic2any";
import helper_functions from "../Config/helper_functions";

export const getBase64FromFile = (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const convertTiffToJpegBlob = (file: File): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const tiffImage = new Tiff({ buffer: new Uint8Array(reader.result as ArrayBuffer) });
        const jpegDataUrl = tiffImage.toCanvas().toDataURL("image/jpeg");
        fetch(jpegDataUrl)
          .then((res) => res.blob())
          .then(resolve)
          .catch(reject);
      } catch (err) {
        console.log("Tiff to jpeg error: ", err);
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

export const convertHeicToJpeg = async (file: File): Promise<File> => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext !== "heic" && ext !== "heif") return file;

  // EWW-448 (extended): a `.HEIC` file is very often already JPEG bytes -- exporting a photo
  // out of iOS (Files, AirDrop, Drive, a Downloads round-trip) commonly transcodes to JPEG
  // while keeping the original filename. `heic2any` is a HEIF decoder and throws on JPEG bytes,
  // so that file was rejected purely because of its name. Peeking the first 3 bytes (JPEG's
  // FF D8 FF magic number) catches that case before handing it to heic2any.
  const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());
  if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) {
    return new File([file], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
  }

  const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 1 });
  const blob = Array.isArray(result) ? result[0] : result;
  const newFileName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
  return new File([blob], newFileName, { type: "image/jpeg" });
};

export const extractExifFromBase64 = (base64: string): Record<string, unknown> => {
  try {
    return piexif.load(base64) as Record<string, unknown>;
  } catch (err) {
    console.warn("EXIF data not found:", err);
    return {};
  }
};

export const validateImageExtension = (fileName: string): string | false => {
  return helper_functions.isImageExtension(fileName);
};

export const getSafeInitialScale = async (blob: Blob | File, defaultScale = 1): Promise<number> => {
  const url = URL.createObjectURL(blob);
  try {
    const d = await helper_functions.getImageDimensionsFromDataUrl(url);
    const maxDim = Math.max(d.width, d.height);
    if (maxDim * defaultScale > 250) return 250 / maxDim;
  } catch {
    // default
  } finally {
    URL.revokeObjectURL(url);
  }
  return defaultScale;
};
